import React, { useMemo, useRef } from 'react';

import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import CardStats from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/components/CardStats';
import {
  getRobloxTopChartsChartXAxisDates,
  transformRobloxTopChartsChartLineSeries,
} from '../../utils/transformRobloxTopChartsChartData';
import { formatRobloxMetricChartYAxisLabel } from '@intelligence/v3/utils/robloxChartAxisFormat';
import { LINE_GRID_TOP } from '@intelligence/pages/TopCharts/_config/echarts';
import { buildRobloxLineChartOption } from '@intelligence/v3/utils/buildRobloxLineChartOption';
import { useRobloxLineChartTooltipFix } from '@intelligence/v3/_hooks/useRobloxLineChartTooltipFix';

import type { IChartsIndexInstance } from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IRobloxTopChartsRow } from '../../types';

import './index.scss';

const CHART_HEIGHT = 350;

interface IRobloxTopChartsLineChartProps {
  loading: boolean;
  chartList: IRobloxTopChartsRow[];
  tableSelection: TableSelectionType[];
  chartMetric: string;
  chartTitle: string;
  granularity?: IGranularity;
}

const RobloxTopChartsLineChart: React.FC<IRobloxTopChartsLineChartProps> = ({
  loading,
  chartList,
  tableSelection,
  chartMetric,
  chartTitle,
  granularity = 'daily',
}) => {
  const lineSeriesList = useMemo(
    () => transformRobloxTopChartsChartLineSeries(chartList, tableSelection, chartMetric),
    [chartList, tableSelection, chartMetric],
  );

  const lineXAxisData = useMemo(
    () => getRobloxTopChartsChartXAxisDates(chartList),
    [chartList],
  );

  const lineSeriesData = useMemo(() => ({
    [chartMetric]: lineSeriesList,
  }), [lineSeriesList, chartMetric]);

  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric: chartMetric,
    lineXAxisData,
    topChartsType: 'detailStorerank',
    imageType: 'region',
    titleNow: chartTitle,
    yAxisFormatter: formatRobloxMetricChartYAxisLabel,
    gridConfig: {
      top: LINE_GRID_TOP,
    },
  });

  const lineOptionCur = useMemo(
    () => buildRobloxLineChartOption(lineOption, granularity),
    [lineOption, granularity],
  );

  const isNoData = useMemo(() => {
    if (!lineSeriesList.length) {
      return true;
    }

    return lineSeriesList.every((seriesItem) => !seriesItem?.history?.length);
  }, [lineSeriesList]);

  const chartRef = useRef<IChartsIndexInstance>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useRobloxLineChartTooltipFix({
    chartRef,
    wrapperRef,
    enabled: !loading && !isNoData,
    deps: [lineOptionCur, chartList, tableSelection, chartMetric],
  });

  if (loading) {
    return (
      <CardStats
        cardStatus={{ isLoading: true }}
        height={CHART_HEIGHT}
      />
    );
  }

  if (isNoData) {
    return (
      <CardStats
        cardStatus={{ isNoData: true }}
        height={CHART_HEIGHT}
      />
    );
  }

  return (
    <div ref={wrapperRef} className="dbi-roblox-top-charts-line-chart card-line">
      <ChartsIndex
        ref={chartRef}
        chartLoading={false}
        lineOption={lineOptionCur}
        chartType="line"
        isNodata={isNoData}
        echartHight={CHART_HEIGHT}
      />
    </div>
  );
};

export default RobloxTopChartsLineChart;
